import type { ReplayData, ReplayFrame } from "@/lib/demo-parser";
import { CT_COLOR, T_COLOR } from "./colors";

export function drawTopBar(
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  frame: ReplayFrame,
  roundStartTick: number,
  replayData: ReplayData
) {
  const ctSideAlive = frame.players.filter((p) => p.team === 3 && p.isAlive).length;
  const tSideAlive = frame.players.filter((p) => p.team === 2 && p.isAlive).length;

  const topBarHeight = 60;
  const topBarY = 20;

  // Background
  ctx.fillStyle = "rgba(0, 0, 0, 0.7)";
  ctx.fillRect(canvas.width / 2 - 250, topBarY, 500, topBarHeight);

  // CT side (left) — boltobserv cyan-blue
  ctx.fillStyle = CT_COLOR;
  ctx.fillRect(canvas.width / 2 - 250, topBarY, 150, topBarHeight);

  // T side (right) — boltobserv golden-yellow
  ctx.fillStyle = T_COLOR;
  ctx.fillRect(canvas.width / 2 + 100, topBarY, 150, topBarHeight);

  // CT count
  ctx.fillStyle = "white";
  ctx.font = "bold 28px sans-serif";
  ctx.textAlign = "center";
  ctx.fillText(ctSideAlive.toString(), canvas.width / 2 - 175, topBarY + 38);
  ctx.font = "bold 10px sans-serif";
  ctx.fillStyle = "rgba(255, 255, 255, 0.7)";
  ctx.fillText("CT", canvas.width / 2 - 175, topBarY + 52);

  // T count
  ctx.fillStyle = "rgba(0, 0, 0, 0.8)";
  ctx.font = "bold 28px sans-serif";
  ctx.fillText(tSideAlive.toString(), canvas.width / 2 + 175, topBarY + 38);
  ctx.font = "bold 10px sans-serif";
  ctx.fillStyle = "rgba(0, 0, 0, 0.7)";
  ctx.fillText("T", canvas.width / 2 + 175, topBarY + 52);

  // Round info
  ctx.fillStyle = "rgba(255, 255, 255, 0.9)";
  ctx.font = "bold 14px sans-serif";
  ctx.fillText(`Round ${frame.roundNumber}`, canvas.width / 2, topBarY + 20);

  // Timer
  let displayText = "";
  let timerColor = "rgba(255, 255, 255, 0.9)";

  // Check if bomb is planted this round and we're past the plant tick
  const plantedThisRound = (replayData.bombsPlanted ?? []).filter(
    (b) => b.tick >= roundStartTick && b.tick <= frame.tick
  );
  const activePlant = plantedThisRound.length > 0
    ? plantedThisRound[plantedThisRound.length - 1]
    : null;

  if (frame.isFreezePeriod) {
    const freezeTime = replayData.freezeTime;
    const ticksIntoRound = Math.max(0, frame.tick - roundStartTick);
    const secondsIntoRound = ticksIntoRound / replayData.tickRate;
    const freezeTimeRemaining = Math.max(0, Math.ceil(freezeTime - secondsIntoRound));
    displayText = `Buy Time: ${freezeTimeRemaining}s`;
    ctx.font = "bold 16px sans-serif";
  } else if (activePlant) {
    // Bomb planted — count down 40s from plant tick
    const BOMB_TIMER = 40;
    const ticksSincePlant = frame.tick - activePlant.tick;
    const secondsSincePlant = ticksSincePlant / replayData.tickRate;
    const secondsRemaining = Math.max(0, Math.floor(BOMB_TIMER - secondsSincePlant));

    // Check for active defuse or completed defuse this round
    const defuseWindow = replayData.tickRate * 10; // 10 seconds window
    const isDefusing = (replayData.bombDefuseStarts ?? []).some(
      (d) => d.tick <= frame.tick && d.tick > frame.tick - defuseWindow && d.tick >= roundStartTick
    );
    const wasDefused = (replayData.bombDefuses ?? []).some(
      (d) => d.tick >= roundStartTick && Math.abs(d.tick - frame.tick) < replayData.tickRate * 3
    );

    if (wasDefused) {
      displayText = "DEFUSED";
      ctx.font = "bold 20px monospace";
      timerColor = "rgba(80, 220, 80, 0.95)";
    } else {
      displayText = `BOMB: ${secondsRemaining}s`;
      ctx.font = "bold 20px monospace";
      // Pulse red when under 10s
      timerColor = secondsRemaining <= 10
        ? `rgba(255, 80, 80, ${0.7 + Math.sin(Date.now() * 0.01) * 0.3})`
        : "rgba(255, 160, 40, 0.95)";
      // Show defusing sub-label below the countdown
      if (isDefusing) {
        ctx.fillStyle = "rgba(80, 200, 255, 0.9)";
        ctx.font = "bold 11px sans-serif";
        ctx.fillText("DEFUSING...", canvas.width / 2, topBarY + 65);
      }
    }
  } else {
    const roundTime = replayData.roundTime;
    // Anchor from the exact freeze_end event tick for this round (not estimated from
    // roundStartTick + freezeTime * tickRate), so the countdown starts at a
    // consistent 1:55 regardless of tick sampling offset.
    const freezeEndTick = (replayData.freezeEndTicks?.[frame.roundNumber - 1])
      ?? (roundStartTick + replayData.tickRate * replayData.freezeTime);
    const roundEndTick = freezeEndTick + replayData.tickRate * roundTime;
    const ticksRemaining = Math.max(0, roundEndTick - frame.tick);
    const secondsRemaining = Math.floor(ticksRemaining / replayData.tickRate);
    if (secondsRemaining === 0) {
      displayText = "Round Over";
      timerColor = "rgba(255, 255, 255, 0.5)";
    } else {
      const minutes = Math.floor(secondsRemaining / 60);
      const seconds = secondsRemaining % 60;
      displayText = `${minutes}:${seconds.toString().padStart(2, "0")}`;
    }
    ctx.font = "bold 20px monospace";
  }

  ctx.fillStyle = timerColor;
  ctx.fillText(displayText, canvas.width / 2, topBarY + 50);
}
